/*
 * Provides an in-chat session browser so conversation history can be managed
 * without occupying permanent navigation space or changing the live session.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import Text from '@amzn/meridian/text';
import chevronLeftSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-left-small';
import { useTranslation } from 'src/arb-wrapper';
import { apiClient } from 'src/clients/api-client';
import {
  useAgentSessionsControllerRemoveMutation,
  useAgentSessionsControllerUpdateMutation,
} from 'src/store/leela-api';
import { ChatHistoryRow } from './ChatHistoryRow';
import {
  compareSessions,
  formatLastModifiedAt,
  getCampaignLabel,
  getSessionTitle,
  SESSION_TITLE_MAX_LENGTH,
  type ChatSession,
} from './chatHistoryView.utils';

export { UNASSIGNED_ENTITY_ID } from './chatHistoryView.utils';

export interface ResumableSession {
  sessionId: string;
  entityId?: string;
}

interface CampaignSummary {
  campaignId: string;
  name?: string;
}

interface PaginatedResponse<T> {
  data?: T[];
}

export interface ChatHistoryViewProps {
  currentUser: string;
  currentEntityId: string | undefined;
  onResume: (session: ResumableSession) => void;
  onClose: () => void;
}

interface SessionEditError {
  sessionId: string;
  messageKey: string;
}

const getTitleValidationMessageKey = (title: string): string | undefined => {
  const trimmedTitle = title.trim();
  if (!trimmedTitle) return 'chat_history_title_required';
  if (trimmedTitle.length > SESSION_TITLE_MAX_LENGTH) return 'chat_history_title_too_long';
  return undefined;
};

export const ChatHistoryView: React.FC<ChatHistoryViewProps> = ({
  currentUser,
  currentEntityId,
  onResume,
  onClose,
}) => {
  const { t } = useTranslation();
  const [deleteAgentSession] = useAgentSessionsControllerRemoveMutation();
  const [updateAgentSession] = useAgentSessionsControllerUpdateMutation();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [campaignNames, setCampaignNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterEntityId, setFilterEntityId] = useState<string>(currentEntityId ?? '');
  const [hasFetchError, setHasFetchError] = useState(false);
  const [deleteErrorSessionId, setDeleteErrorSessionId] = useState<string>();
  const [editingSessionId, setEditingSessionId] = useState<string>();
  const [titleDraft, setTitleDraft] = useState('');
  const [editError, setEditError] = useState<SessionEditError>();
  const saveGenerations = useRef(new Map<string, number>());

  const PAGE_SIZE = 20;

  const fetchSessions = useCallback(async (page = 1, append = false) => {
    if (!currentUser) {
      setIsLoading(false);
      setHasFetchError(false);
      return;
    }

    if (page === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setHasFetchError(false);

    try {
      const params: Record<string, string | number> = { userId: currentUser, page, pageSize: PAGE_SIZE };
      if (filterEntityId) {
        params.entityId = filterEntityId;
        params.entityType = 'campaign';
      }
      const response = await apiClient.get<PaginatedResponse<ChatSession>>('/agent-sessions', { params });
      const fetched = response.data.data ?? [];
      if (append) {
        setSessions((prev) => [...prev, ...fetched]);
      } else {
        setSessions(fetched);
      }
      setHasMore(fetched.length >= PAGE_SIZE);
      setCurrentPage(page);
    } catch {
      if (!append) {
        setHasFetchError(true);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [currentUser, filterEntityId]);

  const handleLoadMore = useCallback(() => {
    void fetchSessions(currentPage + 1, true);
  }, [currentPage, fetchSessions]);

  const handleFilterChange = useCallback((value: string) => {
    setFilterEntityId(value);
  }, []);

  const fetchCampaignNames = useCallback(async () => {
    try {
      const response = await apiClient.get<PaginatedResponse<CampaignSummary>>('/campaigns');
      const names = new Map<string, string>();
      response.data.data?.forEach(({ campaignId, name }) => {
        if (name) names.set(campaignId, name);
      });
      setCampaignNames(names);
    } catch {
      setCampaignNames(new Map());
    }
  }, []);

  useEffect(() => {
    void fetchSessions();
    void fetchCampaignNames();
  }, [fetchCampaignNames, fetchSessions]);

  const visibleSessions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return sessions
      .filter((session) => {
        if (!query) return true;
        const campaignLabel = getCampaignLabel(session, campaignNames) ?? '';
        return (session.title ?? '').toLowerCase().includes(query) || campaignLabel.toLowerCase().includes(query);
      })
      .sort((first, second) => compareSessions(first, second, currentEntityId));
  }, [campaignNames, currentEntityId, searchQuery, sessions]);

  const handleDelete = async (sessionId: string) => {
    setDeleteErrorSessionId(undefined);

    try {
      await deleteAgentSession({ sessionId }).unwrap();
      setSessions((currentSessions) => currentSessions.filter((session) => session.sessionId !== sessionId));
    } catch {
      setDeleteErrorSessionId(sessionId);
    }
  };

  const handleEdit = (session: ChatSession) => {
    setEditingSessionId(session.sessionId);
    setTitleDraft(getSessionTitle(session));
    setEditError(undefined);
  };

  const handleTitleChange = (sessionId: string, title: string) => {
    setTitleDraft(title);
    const messageKey = getTitleValidationMessageKey(title);
    setEditError(messageKey ? { sessionId, messageKey } : undefined);
  };

  const handleCancelEdit = () => {
    setEditingSessionId(undefined);
    setTitleDraft('');
    setEditError(undefined);
  };

  const handleSaveEdit = async (session: ChatSession) => {
    const title = titleDraft.trim();
    const messageKey = getTitleValidationMessageKey(title);
    if (messageKey) {
      setEditError({ sessionId: session.sessionId, messageKey });
      return;
    }

    if (title === getSessionTitle(session).trim()) {
      handleCancelEdit();
      return;
    }

    const { sessionId } = session;
    const saveGeneration = (saveGenerations.current.get(sessionId) ?? 0) + 1;
    saveGenerations.current.set(sessionId, saveGeneration);
    const previousTitle = session.title;
    setEditError(undefined);
    setSessions((currentSessions) => currentSessions.map((currentSession) =>
      currentSession.sessionId === sessionId ? { ...currentSession, title } : currentSession,
    ));
    setEditingSessionId(undefined);
    setTitleDraft('');

    try {
      await updateAgentSession({
        sessionId,
        updateAgentSessionDto: { title },
      }).unwrap();
    } catch {
      if (saveGenerations.current.get(sessionId) !== saveGeneration) return;

      setSessions((currentSessions) => currentSessions.map((currentSession) =>
        currentSession.sessionId === sessionId
          ? { ...currentSession, title: previousTitle }
          : currentSession,
      ));
      setEditError({ sessionId, messageKey: 'chat_history_edit_error' });
    }
  };

  const handleResume = (session: ChatSession) => {
    onResume({ sessionId: session.sessionId, entityId: session.entityId });
  };

  return (
    <Column data-testid="chat-history-view" spacing="300" spacingInset="300">
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Button data-testid="chat-history-close" size="small" type="link" onClick={onClose}>
          <Icon tokens={chevronLeftSmallTokens} /> {t('chat_history_close')}
        </Button>
        <Text type="h300">{t('chat_history_title')}</Text>
      </Row>

      <SearchField
        data-testid="chat-history-search"
        placeholder={t('chat_history_search_placeholder')}
        size="small"
        value={searchQuery}
        width="100%"
        onChange={setSearchQuery}
        onSubmit={() => {}}
      />

      {currentEntityId && (
        <Row alignmentVertical="center" data-testid="chat-history-campaign-filter" spacing="200" spacingInset="200 100 none">
          <Text color={filterEntityId ? 'primary' : 'secondary'} type="b300">
            <span
              data-testid="chat-history-filter-current"
              role="button"
              style={{
                cursor: filterEntityId ? 'default' : 'pointer',
                fontWeight: filterEntityId ? 700 : 400,
                borderBottom: filterEntityId ? '2px solid currentColor' : '2px solid transparent',
                paddingBottom: 2,
              }}
              tabIndex={0}
              onClick={() => { if (!filterEntityId) handleFilterChange(currentEntityId); }}
              onKeyDown={(e) => { if (!filterEntityId && (e.key === 'Enter' || e.key === ' ')) handleFilterChange(currentEntityId); }}
            >
              {t('chat_history_filter_current')}
            </span>
          </Text>
          <Text color="secondary" type="b300">|</Text>
          <Text color={!filterEntityId ? 'primary' : 'secondary'} type="b300">
            <span
              data-testid="chat-history-filter-all"
              role="button"
              style={{
                cursor: !filterEntityId ? 'default' : 'pointer',
                fontWeight: !filterEntityId ? 700 : 400,
                borderBottom: !filterEntityId ? '2px solid currentColor' : '2px solid transparent',
                paddingBottom: 2,
              }}
              tabIndex={0}
              onClick={() => { if (filterEntityId) handleFilterChange(''); }}
              onKeyDown={(e) => { if (filterEntityId && (e.key === 'Enter' || e.key === ' ')) handleFilterChange(''); }}
            >
              {t('chat_history_filter_all')}
            </span>
          </Text>
        </Row>
      )}

      {currentEntityId && <hr style={{ border: 'none', borderTop: '1px solid #eaeded', margin: 0, width: '100%' }} />}

      {isLoading
        ? (
        <Row alignmentHorizontal="center" data-testid="chat-history-loading">
          <Loader size="large" />
        </Row>
          )
        : hasFetchError
          ? (
        <Column alignmentHorizontal="center" data-testid="chat-history-fetch-error" spacing="200">
          <Text color="error">{t('chat_history_fetch_error')}</Text>
          <Button data-testid="chat-history-retry" size="small" type="secondary" onClick={() => { void fetchSessions(); }}>
            {t('chat_history_retry')}
          </Button>
        </Column>
            )
          : (
        <Column spacing="none">
          {visibleSessions.length === 0
            ? <Row spacingInset="300"><Text color="secondary">{t('chat_history_empty')}</Text></Row>
            : visibleSessions.map((session) => {
              const campaignLabel = getCampaignLabel(session, campaignNames);
              const formattedDate = formatLastModifiedAt(session.lastModifiedAt);
              return (
                <ChatHistoryRow
                  campaignLabel={campaignLabel}
                  editErrorMessageKey={editError?.sessionId === session.sessionId ? editError.messageKey : undefined}
                  formattedDate={formattedDate}
                  hasDeleteError={deleteErrorSessionId === session.sessionId}
                  isEditing={editingSessionId === session.sessionId}
                  key={session.sessionId}
                  session={session}
                  titleDraft={titleDraft}
                  onCancelEdit={handleCancelEdit}
                  onDelete={async () => { await handleDelete(session.sessionId); }}
                  onEdit={() => { handleEdit(session); }}
                  onResume={() => { handleResume(session); }}
                  onSaveEdit={async () => { await handleSaveEdit(session); }}
                  onTitleChange={(title) => { handleTitleChange(session.sessionId, title); }}
                />
              );
            })}
          {hasMore && (
            <Row alignmentHorizontal="center" spacingInset="300">
              {isLoadingMore
                ? <Loader size="small" />
                : (
                <Button data-testid="chat-history-load-more" size="small" type="tertiary" onClick={handleLoadMore}>
                  {t('chat_history_load_more')}
                </Button>
                  )}
            </Row>
          )}
        </Column>
            )}
    </Column>
  );
};
